import { Tag, TagLabel, TagLeftIcon } from '@chakra-ui/react';
import React from 'react';
import { z } from 'zod';

import {
  date as commonDate,
  typeguards as commonTypeguards,
  utils as commonUtils,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { AttributeTypeGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { User, UserType } from '~/contexts/UserAuthProvider';
import { Icon } from '~/packages/common-frontend/components/icons';

// Types
export type FacilitiesTableRowData = {
  creditFacilityId: string;
  creditFacilityName: string;
  externalFacilityId: string | null;
  maturityDate: Util.FullDateStr;
  closingDate: Util.FullDateStr;
  globalCommitmentAmount: number;
  globalLoanOutstanding: number;
  globalUnusedCommitment: number;
  globalUtilization: number;
  revolvingPeriodEndDate?: Util.FullDateStr;

  capitalProviderCommitmentAmount: number;
  capitalProviderLoanOutstanding: number;
  capitalProviderUnused: number;
  capitalProviderUtilization: number;

  dateOfLastFundingRequest?: Util.FullDateStr;
  collectionsAccountBalance: number;
  borrowingBaseFromReceivables: number;
  receivableAdvanceRate: string | null; // Null indicates formula
  borrowingBaseCashContribution: number;
  requiredReserve: number;
  totalBorrowingBase: number;
  borrowingBaseAvailability: number;
  interestRates: string;
  unusedFees: string;
  upfrontFees: string;
  accruedInterestAndFees: number;
  financialCovenantsFailed: number | null; // 0 failed means pass, null means no calculations exist
  portfolioCovenantsFailed: number | null; // 0 failed means pass, null means no calculations exist
};
export type FacilitiesTableTotalRowData = Omit<
  FacilitiesTableRowData,
  'maturityDate' | 'closingDate'
> & { maturityDate: null; closingDate: null };

export type FacilityTableColumnKey = keyof FacilitiesTableRowData;
export type FacilityTableRowConfig = {
  header: string;
  fixed?: boolean;
  userTypes: UserType[];
  render: (cellValue: unknown) => React.ReactNode;
  relatedToBorrowingBase: boolean;
};

// Todo: Valley sls Dealing with optional externalFacilityId as a column
type FacilitiesTableConfig = Omit<
  Record<FacilityTableColumnKey, FacilityTableRowConfig>,
  'externalFacilityId' | 'accruedInterestAndFees'
> & {
  /**
   * Currently adjusting visibility of the below for SLS Agent Lender
   */
  externalFacilityId?: FacilityTableRowConfig;
  accruedInterestAndFees?: FacilityTableRowConfig;
};

// Helpers
export const renderDollarCell = (cellValue: unknown) => {
  const number = z.number().parse(cellValue);
  const formatted = commonUtils.numberToCurrencyWithSymbol({ number });
  return formatted ?? number;
};
const renderStringCell = (cellValue: unknown) => z.string().parse(cellValue);

const renderMaybeStringCell = (cellValue: unknown) =>
  cellValue ? renderStringCell(cellValue) : '-';

const renderDateCell = (cellValue: unknown) =>
  commonTypeguards.isFullDateStr(cellValue)
    ? commonDate.fullDateStrToHumanDateString(cellValue)
    : '-';

const renderPercentCell = (cellValue: unknown) => {
  const number = z.number().parse(cellValue);
  const formatted = commonUtils.numberToFormattedString(
    number,
    'percentage',
    false,
  );
  return formatted ?? number;
};

const renderMaybeNumberCell = (cellValue: unknown) => {
  if (!cellValue) {
    return '-';
  }
  const number = z.number().parse(cellValue);
  return String(number);
};

const renderMaybeBooleanCell = (cellValue: unknown) => {
  if (!cellValue) {
    return '-';
  }
  const bool = z.boolean().parse(cellValue);

  switch (bool) {
    case true:
      return 'true';
    case false:
      return 'false';
    default:
      return finleyAssertUnreachable(bool);
  }
};

const renderMaybePercentageCell = (cellValue: unknown) => {
  if (!cellValue) {
    return '-';
  }

  return renderPercentCell(cellValue);
};

const renderMaybeFullDateStrCell = (cellValue: unknown) => {
  if (!cellValue) {
    return '-';
  }
  const formatted = Util.ZodFullDateStrStruct.parse(cellValue);
  return formatted;
};

export const getAttributeValueRenderMethod = (
  type: AttributeTypeGQLType,
): ((cellValue: unknown) => React.ReactNode) => {
  switch (type) {
    case 'boolean':
      return renderMaybeBooleanCell;
    case 'integer':
      return renderMaybeNumberCell;
    case 'float':
      return renderMaybeNumberCell;
    case 'percentageRate':
      return renderMaybePercentageCell;
    case 'string':
      return renderMaybeStringCell;
    case 'fullDateStr':
      return renderMaybeFullDateStrCell;
    case 'stringArray':
      return (cellValue: unknown) =>
        renderMaybeStringCell(
          Array.isArray(cellValue) ? cellValue.join('\n') : '', // @attribute - for now we will render with \n separator in portfolio table
        );
    default:
      return finleyAssertUnreachable(type);
  }
};

const renderStatusCell = (cellValue: unknown) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  if (typeof cellValue !== 'number') return '-';
  const isError = cellValue > 0;
  return (
    <Tag
      size="sm"
      variant="solid-muted"
      colorScheme={isError ? 'red' : 'green'}
    >
      {isError ? (
        <>
          <TagLeftIcon>
            <Icon icon="circle-x" />
          </TagLeftIcon>
          <TagLabel>{cellValue} Failed</TagLabel>
        </>
      ) : (
        <>
          <TagLeftIcon>
            <Icon icon="circle-check" />
          </TagLeftIcon>
          <TagLabel>Passed</TagLabel>
        </>
      )}
    </Tag>
  );
};

// Exports
export const FACILITIES_TABLE_CONFIG = (
  _user: User,
  isSlsAgentLender: boolean,
): FacilitiesTableConfig => {
  return {
    creditFacilityId: {
      header: 'Facility ID',
      fixed: true,
      userTypes: [], // Do not render
      render: renderStringCell,
      relatedToBorrowingBase: false,
    },

    creditFacilityName: {
      header: 'Facility',
      fixed: true,
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderStringCell,
      relatedToBorrowingBase: false,
    },

    ...(isSlsAgentLender && {
      externalFacilityId: {
        header: 'External Facility ID',
        userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
        render: renderMaybeStringCell,
        relatedToBorrowingBase: false,
      },
    }),

    globalCommitmentAmount: {
      header: 'Global Commitment Amount',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: false,
    },

    globalLoanOutstanding: {
      header: 'Global Outstanding Balance',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: false,
    },

    globalUnusedCommitment: {
      header: 'Global Unused Commitment',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    globalUtilization: {
      header: 'Global Utilization',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderPercentCell,
      relatedToBorrowingBase: true,
    },

    capitalProviderCommitmentAmount: {
      header: '{organizationName} Commitment Amount',
      userTypes: ['CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    capitalProviderLoanOutstanding: {
      header: '{organizationName} Outstanding Balance',
      userTypes: ['CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    capitalProviderUnused: {
      header: '{organizationName} Unused Commitment',
      userTypes: ['CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    capitalProviderUtilization: {
      header: '{organizationName} Utilization',
      userTypes: ['CAPITAL_PROVIDER'],
      render: renderPercentCell,
      relatedToBorrowingBase: true,
    },

    collectionsAccountBalance: {
      header: 'Collections Account Balance',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    requiredReserve: {
      header: 'Required Reserve',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    borrowingBaseAvailability: {
      header: 'Borrowing Base Availability',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    totalBorrowingBase: {
      header: 'Total Borrowing Base',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    borrowingBaseFromReceivables: {
      header: 'Borrowing Base from Receivables',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    borrowingBaseCashContribution: {
      header: 'Borrowing Base from Cash',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    receivableAdvanceRate: {
      header: 'Receivable Advance Rate',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderMaybeStringCell,
      relatedToBorrowingBase: true,
    },

    ...(!isSlsAgentLender && {
      accruedInterestAndFees: {
        header: 'Accrued Interest and Fees',
        userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
        render: renderDollarCell,
        relatedToBorrowingBase: false,
      },
    }),

    interestRates: {
      header: 'Interest Rate',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderStringCell,
      relatedToBorrowingBase: false,
    },

    unusedFees: {
      header: 'Unused Fee',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderStringCell,
      relatedToBorrowingBase: false,
    },

    upfrontFees: {
      header: 'Upfront Fee',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderStringCell,
      relatedToBorrowingBase: false,
    },

    closingDate: {
      header: 'Closing Date',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDateCell,
      relatedToBorrowingBase: false,
    },

    maturityDate: {
      header: 'Maturity Date',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDateCell,
      relatedToBorrowingBase: false,
    },

    revolvingPeriodEndDate: {
      header: 'Revolving Period End Date',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderMaybeStringCell,
      relatedToBorrowingBase: false,
    },

    dateOfLastFundingRequest: {
      header: 'Date of Last Funding Request',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDateCell,
      relatedToBorrowingBase: true,
    },

    financialCovenantsFailed: {
      header: 'Financial Covenants',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderStatusCell,
      relatedToBorrowingBase: true,
    },

    portfolioCovenantsFailed: {
      header: 'Portfolio Covenants',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderStatusCell,
      relatedToBorrowingBase: true,
    },
  };
};
